// MembersTab.tsx (Facilitator-scoped, view-only)
import { useCallback, useEffect, useState } from 'react';
import {
  Avatar, Badge, Card, Center, Group, SimpleGrid,
  Stack, Text, ThemeIcon, ActionIcon, Tooltip,
} from '@mantine/core';
import { IconUsers, IconMail, IconPhone, IconRefresh } from '@tabler/icons-react';
import { useFacilitatorPortalService, FacilitatorParticipantView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { MemberDetailModal, MemberDetailFields } from '@/components';

interface MembersTabProps {
  registrationId: number;
  active: boolean;
}

const fullName = (p: FacilitatorParticipantView) =>
  p.fullName?.trim() || [p.name, p.surname].filter(Boolean).join(' ').trim() || 'Unnamed Participant';

const getInitials = (p: FacilitatorParticipantView) =>
  [p.name?.[0], p.surname?.[0]].filter(Boolean).join('').toUpperCase() || 'M';

/**
 * Maps the portal's participant shape onto the modal's permissive field set.
 *
 * ⚠️ `FacilitatorParticipantView` only carries name/surname/email/phone/isMain — it has no
 * telephone, alternative contact, gender, date of birth, member code, or member ID. We don't hide
 * those rows: leaving them unset here means the modal's own "Not on record" fallback renders them,
 * same as it would for an admin-side member missing that data. This keeps the modal visually
 * identical across both contexts instead of two different layouts.
 */
const toDetailFields = (p: FacilitatorParticipantView): MemberDetailFields => ({
  firstName: p.name?.trim() || p.fullName?.trim() || '',
  surname: p.surname ?? '',
  email: p.email ?? null,
  cellNumber: p.phone ?? null,
  isMain: p.isMain,
});

// ── Member grid card ──────────────────────────────────────────────────────
function MemberCard({
  participant,
  onOpenDetails,
}: {
  participant: FacilitatorParticipantView;
  onOpenDetails: () => void;
}) {
  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      h="100%"
      onClick={onOpenDetails}
      style={{ cursor: 'pointer' }}
    >
      <Group justify="center" mb="sm">
        <Avatar alt={fullName(participant)} size="xl" radius="md">
          {getInitials(participant)}
        </Avatar>
      </Group>

      <Text fw={700} ta="center" size="md" mb={4} lineClamp={1}>
        {fullName(participant)}
      </Text>

      {participant.isMain && (
        <Group justify="center" mb={4}>
          <Badge size="sm" variant="light" color="blue">Main Participant</Badge>
        </Group>
      )}

      <Stack align="center" gap={2} mt="xs">
        {participant.email ? (
          <Group gap={6} wrap="nowrap">
            <IconMail size={13} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
            <Text size="sm" c="dimmed">{participant.email}</Text>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">No email on record</Text>
        )}
        {participant.phone ? (
          <Group gap={6} wrap="nowrap">
            <IconPhone size={13} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
            <Text size="sm" c="dimmed">{participant.phone}</Text>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">No phone on record</Text>
        )}
      </Stack>
    </Card>
  );
}

function MemberCardSkeleton() {
  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm">
        <Avatar radius="xl" size={52} color="gray">M</Avatar>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Loading…</Text>
          <Text size="xs" c="dimmed">—</Text>
          <Text size="xs" c="dimmed">—</Text>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function MembersTab({ registrationId, active }: MembersTabProps) {
  const portalService = useFacilitatorPortalService();

  const [members, setMembers] = useState<FacilitatorParticipantView[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  /** Card click opens the read-only detail modal. */
  const [detailParticipant, setDetailParticipant] = useState<FacilitatorParticipantView | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!registrationId) return;
    setLoadingMembers(true);
    try {
      const res = await portalService.participants(registrationId);
      setMembers(res ?? []);
    } catch (e) {
      console.error('Error fetching participants:', e);
      toast.error('Error', 'Could not load participants.');
    } finally {
      setLoadingMembers(false);
    }
  }, [registrationId, portalService]);

  useEffect(() => { if (active) fetchMembers(); }, [active, fetchMembers]);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm" c="dimmed">
            Members on this registration ({members.length})
          </Text>

          <Tooltip label="Refresh members" withArrow>
            <ActionIcon
              variant="subtle"
              onClick={fetchMembers}
              loading={loadingMembers}
              size="lg"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {loadingMembers ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <MemberCardSkeleton />
            <MemberCardSkeleton />
            <MemberCardSkeleton />
          </SimpleGrid>
        ) : members.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={56} radius="xl" variant="light" color="teal">
                <IconUsers size={30} />
              </ThemeIcon>
              <Text fw={600}>No members yet</Text>
              <Text size="sm" c="dimmed" ta="center" maw={360}>
                No participants have been added to this registration yet.
              </Text>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {members.map((p, i) => (
              <MemberCard
                key={`${p.registrationID}-${i}`}
                participant={p}
                onOpenDetails={() => setDetailParticipant(p)}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      <MemberDetailModal
        member={detailParticipant ? toDetailFields(detailParticipant) : null}
        opened={detailParticipant !== null}
        onClose={() => setDetailParticipant(null)}
        contextLabel="registration"
      />
    </Stack>
  );
}